import React, { useMemo } from 'react';

import { HStack, Primitive } from '@rtkwlf/fenrir-react';
import { useTranslation } from 'react-i18next';

import {
  getDefaultDuration,
  getDurationList,
  getStartEndDateToPassToDetailsPage,
} from '@packages/utils/durationSelector/durationSelector.utils';
import type { DurationType } from '@packages/utils/durationSelector/durationSelector.utils';

import { SimpleLoader } from '../simple-loader/simple-loader';

import { DetailLinkIcon } from './DetailLinkIcon';
import { Title } from './Title';

interface HeaderFeatures {
  filter?: {
    showIcon?: boolean;
    details?: Record<string, unknown>;
  };
  details?: {
    showIcon?: boolean;
    labelKey?: string;
    to?: string;
  };
  export?: {
    showIcon?: boolean;
    exportFileName?: string;
  };
}

export type HeaderType = {
  onDetailsClick?: () => void;
  titleKey?: string;
  subTitleKey?: string;
  titleArgs?: Record<string, unknown>;
  features?: HeaderFeatures;
};

interface HeaderPropsType
  extends React.ComponentPropsWithoutRef<typeof Primitive.div> {
  details: HeaderType;
  isLoading?: boolean;
  duration?: DurationType;
}

const TICKETS_ROUTE = '/tickets';

export const Header: React.FC<HeaderPropsType> = (props) => {
  const {
    children,
    details: {
      titleKey,
      titleArgs,
      subTitleKey,
      features,
      onDetailsClick = undefined,
    },
    isLoading,
    duration,
    alignItems = 'flex-start',
    ...rest
  } = props;
  const { t } = useTranslation();

  const defaultDuration = getDefaultDuration(duration?.storageKey);
  const { startTime, endTime } = getStartEndDateToPassToDetailsPage(
    duration?.storageKey,
  );

  const showDetailsIcon = features?.details?.showIcon;
  const detailsTo = features?.details?.to ?? '/';
  const detailsLabelKey = features?.details?.labelKey;

  const path = useMemo(() => {
    const passDurationToDetailsView =
      duration?.passDurationToDetailsView ?? true;
    if (passDurationToDetailsView) {
      if (detailsTo.includes(TICKETS_ROUTE)) {
        const [lastUpdated] = getDurationList(t, [defaultDuration]);
        return detailsTo.includes('?')
          ? `${detailsTo}&lastUpdated=${lastUpdated.zendeskValue}`
          : `${detailsTo}?lastUpdated=${lastUpdated.zendeskValue}`;
      }
      return detailsTo.includes('?')
        ? `${detailsTo}&start=${encodeURIComponent(
            startTime,
          )}&end=${encodeURIComponent(endTime)}`
        : `${detailsTo}?start=${encodeURIComponent(
            startTime,
          )}&end=${encodeURIComponent(endTime)}`;
    }
    return detailsTo;
  }, [
    duration?.passDurationToDetailsView,
    detailsTo,
    startTime,
    endTime,
    t,
    defaultDuration,
  ]);

  return (
    <Primitive.div
      display="flex"
      borderRadius="default"
      padding="medium"
      alignItems={alignItems}
      justifyContent="space-between"
      {...rest}
    >
      <HStack>
        <Title
          titleKey={titleKey}
          titleArgs={titleArgs}
          subTitleKey={subTitleKey}
        />
        {isLoading && <SimpleLoader />}
      </HStack>

      <Primitive.div display="flex" alignItems="center">
        {children}
        {showDetailsIcon && (
          <DetailLinkIcon
            to={path}
            labelKey={detailsLabelKey}
            onClick={onDetailsClick}
          />
        )}
      </Primitive.div>
    </Primitive.div>
  );
};
